import { eventEmitter } from '../../index';
import { tokenManager } from 'core/redux/api/authorization';
import { EventEmitterMessages } from 'constants/event-emitter';

type Notification = {
  zoneGuardStateInfo: { deviceId: number; zoneGuardId: number };
};

type NotificationMessage = {
  '@type': string;
  batchSize: number;
  notification: Notification;
};

type Subscription = {
  '@type': string;
  processorId: number;
};

export class WsService {
  private ws: WebSocket | null = null;

  private batchSize: number | null = null;
  private receivedCount = 0;

  private reconnectTimer: NodeJS.Timeout | null = null;
  private manuallyClosed = false;

  /**
   * processorId -> number of widgets currently interested in it
   */
  private subscriptions = new Map<number, number>();

  private resetBatch() {
    this.batchSize = null;
    this.receivedCount = 0;
  }

  private setBatchSize(size: number) {
    this.batchSize = size;
    this.receivedCount = 0;
  }

  private acknowledgeBatch() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.batchSize === null) {
      return;
    }

    this.ws.send(JSON.stringify({ '@type': 'NotificationAcknowledge', quantity: this.batchSize }));

    this.resetBatch();
  }

  private createSubscription(processorId: number): Subscription {
    return { '@type': 'ZoneGuardStateSubscription', processorId };
  }

  subscribeToZoneGuardOccupancy(processorId: number) {
    const currentCount = this.subscriptions.get(processorId) ?? 0;

    this.subscriptions.set(processorId, currentCount + 1);

    if (currentCount === 0) {
      this.send({ '@type': 'Subscribe', subscription: this.createSubscription(processorId) });
    }
  }

  unsubscribeZoneGuardOccupancy(processorId: number) {
    const currentCount = this.subscriptions.get(processorId);

    if (!currentCount) {
      return;
    }

    if (currentCount > 1) {
      this.subscriptions.set(processorId, currentCount - 1);
      return;
    }

    this.subscriptions.delete(processorId);

    this.send({ '@type': 'Unsubscribe', subscription: this.createSubscription(processorId) });
  }

  closeConnection() {
    this.manuallyClosed = true;
    this.resetBatch();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'manual');
      this.ws = null;
    }
  }

  private scheduleReconnect() {
    if (this.manuallyClosed || this.reconnectTimer) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 3000);
  }

  private send(payload: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.ws.send(JSON.stringify(payload));
  }

  connect() {
    if (this.ws) {
      return;
    }

    this.manuallyClosed = false;

    const token = tokenManager.getAccessToken()?.jwt;

    if (!token) {
      return;
    }

    this.ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/v3/datacaster`, [
      'ow-auth-token',
      `ow-auth-token-${token}`,
    ]);

    this.ws.onopen = () => {
      this.resetBatch();

      console.log('WS connection opened');

      this.subscriptions.forEach((_count, processorId) => {
        this.send({ '@type': 'Subscribe', subscription: this.createSubscription(processorId) });
      });
    };

    this.ws.onmessage = (event) => {
      let data: NotificationMessage;

      try {
        data = JSON.parse(event.data);
      } catch (err) {
        console.error('❌ WS parse error:', err);
        return;
      }

      if (this.batchSize === null) {
        this.setBatchSize(data.batchSize);
      }

      if (data.notification && 'zoneGuardStateInfo' in data.notification) {
        eventEmitter.emit(EventEmitterMessages.ZoneOccupancyUpdate, data.notification.zoneGuardStateInfo.zoneGuardId);
      }

      this.receivedCount++;

      if (this.batchSize && this.receivedCount >= this.batchSize) {
        this.acknowledgeBatch();
      }
    };

    this.ws.onerror = (err) => {
      console.log('❌ WebSocket error:', err);
    };

    this.ws.onclose = () => {
      this.ws = null;
      this.resetBatch();

      if (!this.manuallyClosed) {
        this.scheduleReconnect();
      }
    };
  }
}
